/**
 * dialog.tsx
 * Shared dialog (modal) component based on Radix UI Dialog primitive.
 *
 * Global behaviors applied to ALL dialogs:
 * - Prevent close on outside click (must use buttons or X)
 * - Draggable: click and drag the header to reposition
 * - Resizable: small grip in the bottom-right corner; drag to grow/shrink
 *   in either axis. The new size is persisted to localStorage so the
 *   dialog re-opens at the user's preferred size. Opt out with
 *   `resizable={false}`. Pass `persistKey="my-dialog"` for an i18n-stable
 *   storage key; absent that, the DialogTitle text is used as the key.
 * - X close button in the top-right corner
 * - Position resets to center on each open
 */

import * as React from "react"
import { XIcon } from "lucide-react"
import { Dialog as DialogPrimitive } from "radix-ui"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"

function Dialog({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Root>) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />
}

function DialogTrigger({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />
}

function DialogPortal({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />
}

function DialogClose({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />
}

function DialogOverlay({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      data-slot="dialog-overlay"
      className={cn(
        "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/50",
        className
      )}
      {...props}
    />
  )
}

/**
 * Hook that adds drag-to-move behavior to a dialog.
 *
 * Performance design:
 *  - The drag offset is written to two CSS custom properties (`--drag-x`,
 *    `--drag-y`) that the centering `translate` reads via `calc()`. Setting
 *    a custom property is a compositor-only update — no layout, no reflow.
 *    Writing `marginLeft`/`marginTop` (the previous approach) reflowed the
 *    entire dialog tree on every frame, which became visibly janky on the
 *    larger dialogs (UserDialog, OrderDialog, etc.).
 *  - `mousemove` events arrive faster than the screen refresh; they're
 *    coalesced into a single DOM write per animation frame via
 *    `requestAnimationFrame`. Multiple moves between frames update the
 *    in-memory ref but only one CSS write hits the element per repaint.
 */
function useDraggable() {
  const contentRef = React.useRef<HTMLDivElement>(null)
  const dragging = React.useRef(false)
  const startMouse = React.useRef({ x: 0, y: 0 })
  const startOffset = React.useRef({ x: 0, y: 0 })
  const offset = React.useRef({ x: 0, y: 0 })
  const pendingRAF = React.useRef<number | null>(null)

  /** Reset drag offset so the next open lands centered. */
  const resetPosition = React.useCallback(() => {
    offset.current = { x: 0, y: 0 }
    if (contentRef.current) {
      contentRef.current.style.setProperty('--drag-x', '0px')
      contentRef.current.style.setProperty('--drag-y', '0px')
    }
  }, [])

  /** Start dragging from the header area. Skips drag init when the user
   *  clicks an interactive child so form controls keep working. */
  const onHeaderMouseDown = React.useCallback((e: React.MouseEvent) => {
    const target = e.target as HTMLElement
    if (target.closest('button, input, select, textarea, [role="combobox"]')) return

    dragging.current = true
    startMouse.current = { x: e.clientX, y: e.clientY }
    startOffset.current = { ...offset.current }
    document.body.style.userSelect = 'none'
    e.preventDefault()
  }, [])

  React.useEffect(() => {
    const flush = () => {
      pendingRAF.current = null
      const el = contentRef.current
      if (!el) return
      el.style.setProperty('--drag-x', `${offset.current.x}px`)
      el.style.setProperty('--drag-y', `${offset.current.y}px`)
    }
    const onMove = (e: MouseEvent) => {
      if (!dragging.current) return
      offset.current = {
        x: startOffset.current.x + (e.clientX - startMouse.current.x),
        y: startOffset.current.y + (e.clientY - startMouse.current.y),
      }
      // Many mousemove events can fire between repaints — schedule a
      // single DOM write per frame instead of writing on every event.
      if (pendingRAF.current === null) {
        pendingRAF.current = requestAnimationFrame(flush)
      }
    }
    const onUp = () => {
      dragging.current = false
      document.body.style.userSelect = ''
    }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
    return () => {
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
      if (pendingRAF.current !== null) {
        cancelAnimationFrame(pendingRAF.current)
        pendingRAF.current = null
      }
    }
  }, [])

  return { contentRef, onHeaderMouseDown, resetPosition }
}

/** Context to pass the drag mousedown handler from DialogContent to DialogHeader */
const DialogDragContext = React.createContext<((e: React.MouseEvent) => void) | null>(null)

/** localStorage prefix shared by every dialog's persisted size record. */
const DIALOG_SIZE_KEY_PREFIX = 'kabbi-dialog-size:'

/** Lower bound enforced on persisted/dragged dimensions so a stale or
 *  malicious storage value can't render the dialog as a thin slice. */
const DIALOG_MIN_W = 320
const DIALOG_MIN_H = 200

/**
 * Hook that adds drag-from-corner resize behaviour to a dialog.
 *
 *  - Why custom instead of CSS `resize: both`? The native browser grip lives
 *    in the bottom-right corner, where dialogs typically place footer
 *    buttons (Cancel / Save) — the buttons swallow pointer events before
 *    the grip can see them. `resize: both` also requires `overflow != visible`
 *    which would clip dropdowns (AddressAutocomplete) that intentionally
 *    paint outside the dialog box.
 *  - Radix centres the content via `translate: calc(-50% …)`. Changing the
 *    element's own width/height re-centres for free, but a 1 px cursor
 *    delta only moves the right/bottom edge by 0.5 px (the dialog grows
 *    symmetrically). We multiply the delta by 2 so the grip visually
 *    tracks the cursor.
 *  - `max-width` / `max-height` from the dialog's className would cap any
 *    growth past the original cap. We strip them on the first interaction.
 *  - Move / up handlers are attached **directly on the grip** (not on the
 *    `window`). `setPointerCapture` on pointerdown then routes every
 *    subsequent event for that pointer to the grip, even when the cursor
 *    leaves its bounds — so the React props fire reliably without relying
 *    on bubbling to `window`. The earlier window-listener implementation
 *    silently lost `pointerup` inside Radix's dialog event tree, which is
 *    why saves stopped reaching localStorage.
 */
function useResizable(
  contentRef: React.RefObject<HTMLDivElement | null>,
  enabled: boolean,
  persistKey: string | undefined,
  open: boolean | undefined,
) {
  const state = React.useRef<{ sx: number; sy: number; sw: number; sh: number; active: boolean }>({
    sx: 0, sy: 0, sw: 0, sh: 0, active: false,
  })

  /**
   * Resolve the localStorage key for this dialog instance. Explicit
   * `persistKey` wins; otherwise we read the rendered DialogTitle text.
   * Title-derived keys aren't i18n-stable (they change with the active
   * language) but they keep "just opt in" simple — callers with a
   * preferred key pass it explicitly.
   */
  const resolveKey = React.useCallback((): string | null => {
    if (persistKey) return DIALOG_SIZE_KEY_PREFIX + persistKey
    const el = contentRef.current
    const title = el?.querySelector('[data-slot="dialog-title"]')?.textContent?.trim()
    return title ? `${DIALOG_SIZE_KEY_PREFIX}auto:${title}` : null
  }, [contentRef, persistKey])

  /** Push w/h onto the element. Caller is responsible for having already
   *  freed `max-width` / `max-height` (see `freeCaps` below) when needed. */
  const applySize = React.useCallback((el: HTMLElement, w: number, h: number) => {
    const ww = Math.min(window.innerWidth  * 0.98, Math.max(DIALOG_MIN_W, w))
    const hh = Math.min(window.innerHeight * 0.98, Math.max(DIALOG_MIN_H, h))
    el.style.width  = `${ww}px`
    el.style.height = `${hh}px`
  }, [])

  /**
   * Free the className-derived caps without causing a visual jump.
   *
   * Why this matters: a dialog like the picker uses
   *   `w-[95vw] max-w-[1800px]`
   * which renders at 1800 px on a 1920 px screen (95vw = 1824 px,
   * capped). The moment we set `el.style.maxWidth = 'none'` the cap
   * disappears and the dialog snaps to 1824 px in a single frame — the
   * "blink to bigger" the user reported. Fix: pin the current pixel
   * dimensions into inline `width` / `height` BEFORE removing the caps,
   * so the visual size doesn't change. Idempotent — safe to call again
   * mid-session.
   */
  const freeCaps = React.useCallback((el: HTMLElement) => {
    const w = el.offsetWidth
    const h = el.offsetHeight
    el.style.width     = `${w}px`
    el.style.height    = `${h}px`
    el.style.maxWidth  = 'none'
    el.style.maxHeight = 'none'
  }, [])

  /**
   * Callback ref that React invokes synchronously the moment the
   * DialogContent DOM node is attached. We use this — not a layout effect
   * — for two reasons:
   *
   * 1. In React 19 + Radix Portal, `useEffect` / `useLayoutEffect` fire
   *    with `contentRef.current === null` (verified via instrumented
   *    logging 2026-05-24). The portal's commit ordering means the ref
   *    isn't populated by the time the passive/layout phase runs in
   *    this branch of the tree.
   * 2. We want the saved size to land BEFORE the first paint to avoid
   *    a flash from the className-derived default size to the saved
   *    size. Callback refs run during commit, before paint.
   *
   * The callback also forwards the node to `contentRef` (the useDraggable
   * mutable ref) so dragging keeps working. `restoredRef` guards against
   * the callback being invoked again on the same node — the restore
   * should happen exactly once per mount.
   */
  const restoredRef = React.useRef(false)
  const handleContentMount = React.useCallback((node: HTMLDivElement | null) => {
    // Forward to the useDraggable ref so header drag still works.
    ;(contentRef as React.MutableRefObject<HTMLDivElement | null>).current = node
    if (!node) {
      restoredRef.current = false  // reset for next mount
      return
    }
    if (!enabled || restoredRef.current) return
    const key = resolveKey()
    if (!key) return
    let raw: string | null = null
    try { raw = localStorage.getItem(key) } catch { return }
    if (!raw) return
    let parsed: { w?: unknown; h?: unknown } | null = null
    try { parsed = JSON.parse(raw) } catch { return }
    if (!parsed || typeof parsed.w !== 'number' || typeof parsed.h !== 'number') return
    freeCaps(node)
    applySize(node, parsed.w, parsed.h)
    restoredRef.current = true
  }, [enabled, contentRef, resolveKey, freeCaps, applySize])

  /**
   * Why React synthetic events here (and not native `addEventListener`)?
   * In this app's stack — React 19 + Radix Dialog Portal + Vite dev — a
   * `useEffect` that tries to read `gripRef.current` or `contentRef.current`
   * still sees them as `null` (verified via instrumented logs 2026-05-24).
   * The Portal mounts its children async enough that the passive effect
   * fires before the refs are populated, so native listeners never attach.
   * React's synthetic event delegation is unaffected — it intercepts at
   * the React root and dispatches based on the rendered element, no ref
   * lookup required.
   */
  const onPointerDown = React.useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!enabled) return
    const el = contentRef.current
    if (!el) return
    // Free the className caps without a visual jump. By the time the user
    // can click the grip, the restore callback (handleContentMount) has
    // already run and populated `contentRef.current`, so this is safe.
    freeCaps(el)
    state.current = {
      sx: e.clientX,
      sy: e.clientY,
      sw: el.offsetWidth,
      sh: el.offsetHeight,
      active: true,
    }
    try { (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId) } catch { /* unsupported — ignore */ }
    document.body.style.userSelect = 'none'
    e.preventDefault()
    e.stopPropagation()
  }, [enabled, contentRef, freeCaps])

  const onPointerMove = React.useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!state.current.active) return
    const el = contentRef.current
    if (!el) return
    const dx = (e.clientX - state.current.sx) * 2
    const dy = (e.clientY - state.current.sy) * 2
    applySize(el, state.current.sw + dx, state.current.sh + dy)
  }, [contentRef, applySize])

  const onPointerUp = React.useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!state.current.active) return
    state.current.active = false
    document.body.style.userSelect = ''
    const el = contentRef.current
    const key = resolveKey()
    if (el && key) {
      try {
        localStorage.setItem(key, JSON.stringify({ w: el.offsetWidth, h: el.offsetHeight }))
      } catch { /* private mode / quota — ignore */ }
    }
    try { (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId) } catch { /* already released */ }
  }, [contentRef, resolveKey])

  return { onPointerDown, onPointerMove, onPointerUp, contentMountRef: handleContentMount }
}

function DialogContent({
  className,
  children,
  showCloseButton = true,
  resizable = true,
  persistKey,
  open,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
  showCloseButton?: boolean
  /** Set to false on tiny / fixed-size dialogs that shouldn't show a grip. */
  resizable?: boolean
  /** Stable storage key. Falls back to DialogTitle text when absent. */
  persistKey?: string
  /** Optional — passing the dialog's `open` retriggers the size-restore
   *  effect on every re-open without us having to mutate the global
   *  Radix state machine. Most callers can omit it. */
  open?: boolean
}) {
  const { contentRef, onHeaderMouseDown, resetPosition } = useDraggable()
  const grip = useResizable(contentRef, resizable, persistKey, open)

  return (
    <DialogPortal data-slot="dialog-portal">
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={grip.contentMountRef}
        data-slot="dialog-content"
        className={cn(
          // The centering `translate` is applied via inline style below so
          // the drag offset can compose with it through CSS variables. The
          // Tailwind `translate-x-[-50%]` utility is intentionally not in
          // this class string for that reason.
          // White card surface (navy theme: --card is #fff, --background is
          // the cool-grey app canvas). Dialogs sit ABOVE the canvas so they
          // must be white — this cascades to every consumer that doesn't
          // override it.
          "bg-card data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 fixed top-[50%] left-[50%] z-50 grid w-full max-w-[calc(100%-2rem)] gap-4 rounded-lg border p-6 shadow-lg duration-200 outline-none sm:max-w-lg",
          className
        )}
        {...props}
        // Compose centering + drag offset into the modern CSS `translate`
        // property. Updating only the CSS vars during a drag is a
        // compositor-only operation, so reflow is not triggered per move.
        // `transitionProperty: 'none'` kills the implicit transition that
        // Tailwind's `duration-200` (intended for the open/close zoom
        // animation) accidentally applies to every property change —
        // without it, each rAF frame's translate update is smoothed over
        // 200 ms, so the dialog visibly trails the cursor like pulled
        // gum. Open/close still animate because those are CSS
        // `animation`s, which `transitionProperty` doesn't affect.
        // Caller-provided styles spread first so this stays the source of
        // truth for positioning.
        style={{
          ...props.style,
          translate: 'calc(-50% + var(--drag-x, 0px)) calc(-50% + var(--drag-y, 0px))',
          transitionProperty: 'none',
        }}
        onInteractOutside={(e) => { e.preventDefault(); props.onInteractOutside?.(e) }}
        onPointerDownOutside={(e) => { e.preventDefault(); props.onPointerDownOutside?.(e) }}
        onAnimationEnd={() => resetPosition()}
      >
        <DialogDragContext.Provider value={onHeaderMouseDown}>
          {children}
        </DialogDragContext.Provider>
        {showCloseButton && (
          <DialogPrimitive.Close
            data-slot="dialog-close"
            className="ring-offset-background focus:ring-ring data-[state=open]:bg-accent data-[state=open]:text-muted-foreground absolute top-4 right-4 rounded-xs opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4"
          >
            <XIcon />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
        {resizable && (
          /* Triangular resize grip hugging the bottom-right corner. 12 px is
             small enough to clear typical footer button padding (footer
             rows use ≥ p-3 / p-6 so the rightmost button starts well above
             this). Pointer events bubble through the gradient's transparent
             half so users can still click anything that happens to sit
             behind the top-left half of the grip's box. */
          <div
            onPointerDown={grip.onPointerDown}
            onPointerMove={grip.onPointerMove}
            onPointerUp={grip.onPointerUp}
            onPointerCancel={grip.onPointerUp}
            data-slot="dialog-resize-grip"
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize"
            title="Drag to resize"
            className="absolute bottom-0 right-0 z-30 h-3 w-3 cursor-nwse-resize opacity-40 hover:opacity-90 touch-none"
            style={{
              background:
                'linear-gradient(135deg, transparent 0 50%, var(--muted-foreground, currentColor) 50% 100%)',
              borderBottomRightRadius: 'inherit',
            }}
          />
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  )
}

function DialogHeader({ className, ...props }: React.ComponentProps<"div">) {
  const onMouseDown = React.useContext(DialogDragContext)
  return (
    <div
      data-slot="dialog-header"
      className={cn("flex flex-col gap-2 text-center sm:text-left cursor-grab active:cursor-grabbing", className)}
      onMouseDown={onMouseDown ?? undefined}
      {...props}
    />
  )
}

function DialogFooter({
  className,
  showCloseButton = false,
  children,
  ...props
}: React.ComponentProps<"div"> & {
  showCloseButton?: boolean
}) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        "flex flex-col-reverse gap-2 sm:flex-row sm:justify-end",
        className
      )}
      {...props}
    >
      {children}
      {showCloseButton && (
        <DialogPrimitive.Close asChild>
          <Button variant="outline">Close</Button>
        </DialogPrimitive.Close>
      )}
    </div>
  )
}

function DialogTitle({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn("text-lg leading-none font-semibold", className)}
      {...props}
    />
  )
}

function DialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn("text-muted-foreground text-sm", className)}
      {...props}
    />
  )
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
}
